import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  MapPin,
  Package,
  Truck,
  ArrowRight,
  TrendingUp,
  Zap,
  Camera,
  Eye,
  Bell,
  Check,
  ClipboardList,
  AlertTriangle,
  Star,
  Settings,
  Smartphone,
  Edit3,
  UserCheck,
  Search,
  RefreshCw,
} from "lucide-react";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const StatusBadge = ({ status, delay = 0 }: { status: string; delay?: number }) => {
  const variants = {
    draft: "bg-gray-100 text-gray-800",
    paid: "bg-green-100 text-green-800",
    finalized: "bg-blue-100 text-blue-800",
    scheduled: "bg-purple-100 text-purple-800",
    inprogress: "bg-yellow-100 text-yellow-800",
    completed: "bg-green-100 text-green-800",
    canceled: "bg-red-100 text-red-800",
  };

  const statusMap = {
    draft: "Draft",
    paid: "Paid",
    finalized: "Finalized",
    scheduled: "Scheduled",
    inprogress: "In Progress",
    completed: "Completed",
    canceled: "Canceled",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`px-3 py-1 rounded-full text-sm font-medium ${variants[status as keyof typeof variants] || variants.draft}`}
    >
      {statusMap[status as keyof typeof statusMap] || status}
    </motion.div>
  );
};

const DashboardMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
              <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
              <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
            </div>
            <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
              Installation Order Dashboard
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
            <Search className="w-4 h-4" />
            <Bell className="w-4 h-4" />
            <div className="w-7 h-7 rounded-full bg-gray-300"></div>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              <CounterAnimation value={23} />
            </div>
            <p className="text-sm text-gray-600">Active Orders</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">
              <CounterAnimation value={89} />
            </div>
            <p className="text-sm text-gray-600">Completed This Month</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold" style={{ color: "#f58b01" }}>
              <CounterAnimation value={12} />
            </div>
            <p className="text-sm text-gray-600">Scheduled Today</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              <CounterAnimation value={156} />
            </div>
            <p className="text-sm text-gray-600">Signs Available</p>
          </Card>
        </div>

        <div className="space-y-3">
          {[
            {
              address: "1234 Beverly Hills Dr",
              status: "scheduled",
              agent: "Sarah Johnson",
              time: "2:00 PM",
            },
            {
              address: "5678 Manhattan Beach Ave",
              status: "inprogress",
              agent: "Mike Chen",
              time: "On-site now",
            },
            {
              address: "9101 Santa Monica Blvd",
              status: "completed",
              agent: "Lisa Rodriguez",
              time: "Completed",
            },
            {
              address: "1213 Culver City St",
              status: "draft",
              agent: "David Kim",
              time: "Pending approval",
            },
          ].map((job, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{job.address}</p>
                  <p className="text-sm text-gray-600">
                    {job.agent} • {job.time}
                  </p>
                </div>
              </div>
              <StatusBadge status={job.status} delay={0.7 + index * 0.1} />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function InstallationOrdersLandingPage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const keyFeatures: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "createManageJob",
      title: "Create & Manage Jobs",
      description:
        "Agents and offices can create installation or removal requests in seconds. All jobs are tracked automatically with clear status updates for everyone involved.",
    },
    {
      icon: "smartSchedulingPriority",
      title: "Smart Scheduling & Priorities",
      description:
        "Schedule installations ahead or as needed, and set job priorities like Low, Standard, or Rush for better control.",
    },
    {
      icon: "flexibleInventoryVendorOptions",
      title: "Flexible Inventory & Vendor Options",
      description:
        "Bring your existing signs into RealEsy and start working with new vendors seamlessly. Use your own inventory for jobs or hand it over to approved vendors for installation and storage.",
    },
    {
      icon: "flexibleHandoverDelivery",
      title: "Flexible Handover Options",
      description:
        "Choose how products are handled — store with vendors, schedule drop-off, request pickup, or use items directly at the job site.",
    },
    {
      icon: "verifiedProofInstallation",
      title: "Verified Proof of Installation",
      description:
        "Installers upload photos and notes from the field. Each job includes verifiable proof and compliance records.",
    },
    {
      icon: "accurateMapPinLocation",
      title: "Accurate Map Pin Locations",
      description:
        "Drop precise pins for installation sites — even unmapped addresses. Minimize errors, wasted trips, and location confusion.",
    },
    {
      icon: "paymentBillingSimple",
      title: "Payments & Billing Made Simple",
      description:
        "Support office-paid or agent-paid models. Offer vendor-approved credit and secure card payments. Platform discounts automatically apply to qualifying sign orders, reducing brokerage costs.",
    },
    {
      icon: "installRealTimeNotification",
      title: "Real-Time Notifications",
      description:
        "Get instant alerts at every stage. Agents and managers stay synced on progress and completion.",
    },
    {
      icon: "mobileAppAccess",
      title: "Mobile App Access",
      description:
        "Create, track, and verify installations anywhere. Manage inventory, view proofs, and control operations on the go.",
    },
    {
      icon: "quickActionsFasterWorkflow",
      title: "Quick Actions for Faster Workflows",
      description:
        "Repeat, edit, or reschedule jobs in one click. Save time for brokers and managers with streamlined controls.",
    },
  ];

  const benefits: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "installSaveTime",
      title: "Save Time",
      description: "Automate scheduling and eliminate back-and-forth coordination.",
    },
    {
      icon: "controlInventory",
      title: "Control Inventory",
      description:
        "Purchase signs directly from approved vendors and track how each one is used, stored, or installed.",
    },
    {
      icon: "installReduceCost",
      title: "Reduce Costs",
      description:
        "Lower expenses with exclusive vendor discounts, optimized orders, and less waste.",
    },
    {
      icon: "ensureQuality",
      title: "Ensure Quality",
      description:
        "Get verified proof of every installation with photo records and detailed job tracking.",
    },
    {
      icon: "scaleOperations",
      title: "Scale Operations",
      description: "Handle more installations and agents without adding overhead.",
    },
  ];

  const faqs = [
    {
      q: "Can I track installations created by my agents?",
      a: "Yes. All agent-level installation jobs are visible in their profiles, giving you complete oversight.",
    },
    {
      q: "What if my vendor handles the pickup?",
      a: "You can select from multiple handover options, including vendor pickup, scheduled drop-off, or storage.",
    },
    {
      q: "Is payment secure?",
      a: "Absolutely — all payments are encrypted and compliant with industry security standards.",
    },
    {
      q: "How does the mobile app work?",
      a: "The mobile app provides full functionality for creating, tracking, and managing installation orders from anywhere.",
    },
    {
      q: "Can I set job priorities?",
      a: "Yes, you can prioritize urgent jobs and use the Jobs Today indicator to balance workloads effectively.",
    },
    {
      q: "What proof of installation do I get?",
      a: "Installers upload photos and detailed notes from the field, providing verifiable proof of completion for every job.",
    },
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* MapPin Icon - Top Left */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              y: [0, -30, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute top-24 left-[8%] w-12 h-12 text-blue-400"
          >
            <MapPin className="w-full h-full" />
          </motion.div>

          {/* Calendar Icon - Top Right */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              y: [0, 25, 0],
              rotate: [0, 10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
            className="absolute top-32 right-[12%] w-10 h-10 text-purple-400"
          >
            <Calendar className="w-full h-full" />
          </motion.div>

          {/* Package Icon - Left Middle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              x: [0, -20, 0],
              y: [0, 15, 0],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            className="absolute top-[40%] left-[5%] w-11 h-11 text-green-400"
          >
            <Package className="w-full h-full" />
          </motion.div>

          {/* Truck Icon - Right Middle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              x: [0, 20, 0],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 6.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
            className="absolute top-[35%] right-[8%] w-14 h-14"
            style={{ color: "#f58b01" }}
          >
            <Truck className="w-full h-full" />
          </motion.div>

          {/* CheckCircle Icon - Bottom Left */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              y: [0, -25, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 5.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2,
            }}
            className="absolute bottom-[15%] left-[15%] w-10 h-10 text-green-500"
          >
            <CheckCircle className="w-full h-full" />
          </motion.div>

          {/* Camera Icon - Bottom Right */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              x: [0, -15, 0],
              y: [0, 20, 0],
              rotate: [0, -8, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2.5,
            }}
            className="absolute bottom-[20%] right-[18%] w-11 h-11 text-purple-500"
          >
            <Camera className="w-full h-full" />
          </motion.div>

          {/* ClipboardList Icon - Top Center Left */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              y: [0, 30, 0],
              x: [0, 10, 0],
            }}
            transition={{
              duration: 7.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.8,
            }}
            className="absolute top-[15%] left-[25%] w-9 h-9 text-blue-500"
          >
            <ClipboardList className="w-full h-full" />
          </motion.div>

          {/* Bell Icon - Top Center Right */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              y: [0, -18, 0],
              rotate: [0, 15, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3,
            }}
            className="absolute top-[20%] right-[28%] w-9 h-9 text-yellow-500"
          >
            <Bell className="w-full h-full" />
          </motion.div>

          {/* Settings Icon - Middle Center */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.2, 0.5, 0.2],
              rotate: [0, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear",
              delay: 1.2,
            }}
            className="absolute top-[45%] left-[50%] w-10 h-10 text-indigo-400 transform -translate-x-1/2"
          >
            <Settings className="w-full h-full" />
          </motion.div>

          {/* Star Icon - Bottom Center */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              y: [0, -15, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3.5,
            }}
            className="absolute bottom-[10%] left-[45%] w-8 h-8 text-yellow-400"
          >
            <Star className="w-full h-full" />
          </motion.div>

          {/* Smartphone Icon - Middle Right */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              y: [0, 22, 0],
              x: [0, -12, 0],
            }}
            transition={{
              duration: 6.2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 4,
            }}
            className="absolute top-[55%] right-[20%] w-9 h-9 text-blue-400"
          >
            <Smartphone className="w-full h-full" />
          </motion.div>

          {/* Zap Icon - Left Side */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              x: [0, 18, 0],
              y: [0, -12, 0],
            }}
            transition={{
              duration: 5.8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2.2,
            }}
            className="absolute top-[60%] left-[18%] w-10 h-10 text-cyan-400"
          >
            <Zap className="w-full h-full" />
          </motion.div>
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    Simplify, Schedule, and Track Every Installation — All in One Place
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    With RealEsy, brokerages and agents can easily request, schedule, and monitor
                    real estate signage installations — from job creation to verified completion,
                    all within a single platform.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                    >
                      Get Started
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Why Installation Orders Matter Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Installation Orders Matter for Brokerages & Agents
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Managing signage installations manually leads to confusion, delays, and higher
                costs.
                <br />
                RealEsy gives brokerages and agents a transparent, automated, and mobile-first way
                to stay in control — from request to verified completion.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-2xl border-2"
              >
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-6">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Problem</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Manual scheduling creates delays and miscommunication.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Missing or misplaced signs increase expenses.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Agents waste time chasing vendor updates.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>No clear proof or record of completed jobs.</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-8 rounded-2xl border-2"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Solution</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Smart scheduling with real-time job tracking.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Complete visibility into sign inventory and job status.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Mobile-first platform for on-the-go management.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Photo verification and digital proof of installation.</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="p-8 rounded-2xl border-2"
              >
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Result</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Faster, error-free installations and removals.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Lower operational costs through better inventory management.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>
                      Exclusive vendor discounts that reduce signage and service expenses.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Happier agents and offices with transparent, reliable workflows.</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Key Features of Installation Orders */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Key Features of Installation Orders
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything agents and offices need to manage signage installations efficiently and
                professionally.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Installation Workflow Visualization */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Visual Order Lifecycle
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Track every installation from draft to completion with our comprehensive workflow
                system.
              </p>
            </div>

            <div className="relative">
              {/* Desktop Workflow */}
              <div className="hidden lg:flex items-center justify-between">
                {(
                  [
                    { status: "paid", icon: "workflowPaid" },
                    { status: "scheduled", icon: "workflowScheduled" },
                    { status: "inprogress", icon: "workflowInProgress" },
                    { status: "completed", icon: "workflowCompleted" },
                  ] as const
                ).map((step, index, steps) => (
                  <React.Fragment key={step.status}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 }}
                      className="flex flex-col items-center"
                    >
                      <div className="w-16 h-16 bg-white rounded-2xl border border-gray-200 shadow-md flex items-center justify-center mb-4">
                        <AppIcon name={step.icon} size={36} alt={step.status} />
                      </div>
                      <StatusBadge status={step.status} delay={index * 0.2 + 0.3} />
                    </motion.div>

                    {index < steps.length - 1 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 1 + index * 0.3, duration: 0.8 }}
                        className="flex-1 h-1 bg-[#236DD9] rounded-full mx-4"
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile Workflow */}
              <div className="lg:hidden space-y-8">
                {(
                  [
                    {
                      status: "paid",
                      icon: "workflowPaid",
                      desc: "Payment processed successfully",
                    },
                    {
                      status: "scheduled",
                      icon: "workflowScheduled",
                      desc: "Installation date and time set",
                    },
                    {
                      status: "inprogress",
                      icon: "workflowInProgress",
                      desc: "Installer on-site working",
                    },
                    {
                      status: "completed",
                      icon: "workflowCompleted",
                      desc: "Installation verified and photos uploaded",
                    },
                  ] as const
                ).map((step, index) => (
                  <motion.div
                    key={step.status}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl border border-gray-200 shadow-md flex items-center justify-center flex-shrink-0">
                      <AppIcon name={step.icon} size={28} alt={step.status} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <StatusBadge status={step.status} delay={index * 0.1 + 0.2} />
                      </div>
                      <p className="text-sm text-gray-600">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Stay Updated in Real-Time Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Stay Updated in Real-Time
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Stay updated with real-time notifications—get alerts for scheduling changes,
                payments, and job completion.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                className="p-8 rounded-2xl border-2 group cursor-pointer"
              >
                <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule Changes</h3>
                <p className="text-gray-600 leading-relaxed">
                  Instant alerts when installations are rescheduled
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                className="p-8 rounded-2xl border-2 group cursor-pointer"
              >
                <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <CreditCard className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Payment Updates</h3>
                <p className="text-gray-600 leading-relaxed">
                  Notifications for successful payments and invoices
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                className="p-8 rounded-2xl border-2 group cursor-pointer"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: "#f58b01" }}
                >
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Job Completion</h3>
                <p className="text-gray-600 leading-relaxed">
                  Real-time alerts when installations are finished
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-center mt-12"
            >
              <div
                className="text-white p-8 rounded-2xl max-w-2xl mx-auto"
                style={{ background: "linear-gradient(180deg, #236DD9 0%, #073399 100%)" }}
              >
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Bell className="w-6 h-6" />
                  <h3 className="text-xl font-bold">Never Miss an Update</h3>
                </div>
                <p className="text-lg text-blue-100">
                  Get instant notifications via email, SMS, or push notifications to stay informed
                  about every aspect of your installation orders.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </FeatureSection>

      {/* Work on the Go with Mobile App Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Work on the Go with the RealEsy Mobile App
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Real estate moves fast — and now your signage operations can too.
                    <br />
                    With the RealEsy Mobile App, agents and offices can create, track, and manage
                    every installation right from their phone.
                  </p>
                </div>

                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">What You Can Do:</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(
                      [
                        {
                          icon: "createOrdersAnywhere",
                          title: "Create Orders Anywhere",
                          description:
                            "Place installation or removal requests instantly while at a property or on the move.",
                        },
                        {
                          icon: "trackProgressRealtime",
                          title: "Track Progress in Real Time",
                          description:
                            "See job status updates and completion proof with photos from the field.",
                        },
                        {
                          icon: "manageInventoryOnSite",
                          title: "Manage Inventory On-Site",
                          description:
                            "Check sign availability, assign items, and confirm handovers on the spot.",
                        },
                        {
                          icon: "editOrReschedule",
                          title: "Edit or Reschedule in Seconds",
                          description:
                            "Use Quick Actions to approve, modify, or cancel jobs with one tap.",
                        },
                        {
                          icon: "stayNotified",
                          title: "Stay Notified",
                          description:
                            "Get instant alerts on scheduling, progress, and completion — wherever you are.",
                        },
                      ] as const
                    ).map((feature, index) => (
                      <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                        className="bg-white p-6 rounded-2xl border border-gray-200 group hover:shadow-lg transition-all duration-300"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                            <AppIcon name={feature.icon} size={28} alt={feature.title} />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 }}
                  className="text-white p-8 rounded-2xl"
                  style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
                >
                  <h3 className="text-xl font-bold mb-4">Why It Matters for Brokerages</h3>
                  <p className="text-lg text-blue-100 leading-relaxed">
                    Empower agents and managers to stay productive anywhere.
                    <br />
                    No waiting, no delays — every job, every update, right in their pocket.
                  </p>
                </motion.div>
              </motion.div>

              {/* Right side: Enhanced Mobile App Mockup */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:pl-8"
              >
                <div className="relative max-w-[360px] mx-auto">
                  {/* Enhanced Background Effects */}
                  <div className="absolute -inset-12 opacity-20">
                    <div className="absolute top-8 left-8 w-24 h-24 bg-blue-400 rounded-full blur-2xl animate-pulse"></div>
                    <div
                      className="absolute bottom-8 right-8 w-28 h-28 bg-purple-400 rounded-full blur-2xl animate-pulse"
                      style={{ animationDelay: "1s" }}
                    ></div>
                    <div
                      className="absolute top-1/2 left-2 w-20 h-20 rounded-full blur-2xl animate-pulse"
                      style={{ background: "#f58b01", animationDelay: "2s" }}
                    ></div>
                  </div>

                  {/* iPhone 17 Pro Simulator Frame */}
                  <motion.div
                    whileHover={{ y: -10, rotateY: 8, rotateX: 5 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative transform-gpu mx-auto w-full max-w-[360px]"
                    style={{ perspective: "1000px" }}
                  >
                    {/* Left side: Action / Silent + Volume Up / Volume Down */}
                    <div
                      className="absolute left-0 top-[88px] -translate-x-[3px] w-[3px] h-[22px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[140px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[190px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />

                    {/* Right side: Power / Side button */}
                    <div
                      className="absolute right-0 top-[160px] translate-x-[3px] w-[3px] h-[64px] bg-[#2c2c2e] rounded-r-[2px] z-30"
                      aria-hidden="true"
                    />

                    <div className="relative bg-[#0b0b0c] rounded-[3.1rem] p-[11px] shadow-[0_25px_60px_rgba(0,0,0,0.35)] transform-gpu ring-1 ring-black/40">
                      <div className="relative bg-[#1a1a1c] rounded-[2.65rem] p-[2px]">
                        {/* Phone Screen */}
                        <div className="bg-[#f5f5f7] rounded-[2.55rem] overflow-hidden relative min-h-[720px]">
                          {/* Status Bar + Dynamic Island */}
                          <div className="relative px-7 pt-4 pb-2.5 flex justify-between items-center z-10">
                            <span className="text-[13px] font-semibold text-gray-900 tracking-tight">
                              9:41
                            </span>
                            <div className="absolute left-1/2 top-2.5 -translate-x-1/2 w-[102px] h-[30px] bg-black rounded-full z-20 flex items-center justify-end pr-3">
                              <motion.div
                                animate={{ scale: [1, 1.15, 1] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="w-2 h-2 rounded-full bg-[#1a1a1a] ring-1 ring-gray-700"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-900">
                              <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor" aria-hidden="true">
                                <rect x="0" y="8" width="2.5" height="4" rx="0.5" />
                                <rect x="4" y="5" width="2.5" height="7" rx="0.5" />
                                <rect x="8" y="2" width="2.5" height="10" rx="0.5" />
                                <rect x="12" y="0" width="2.5" height="12" rx="0.5" />
                              </svg>
                              <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor" aria-hidden="true">
                                <path d="M7.5 2.2c1.9 0 3.6.7 4.9 1.9l-.9.9A5.3 5.3 0 0 0 7.5 3.5 5.3 5.3 0 0 0 3.5 5l-.9-.9A6.9 6.9 0 0 1 7.5 2.2zm0 2.4c1.2 0 2.3.5 3.1 1.2l-.9.9a2.9 2.9 0 0 0-4.4 0l-.9-.9A4.3 4.3 0 0 1 7.5 4.6zM7.5 9.2a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" />
                              </svg>
                              <svg width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
                                <rect x="0.5" y="0.5" width="20" height="11" rx="2.5" stroke="currentColor" strokeOpacity="0.35" />
                                <rect x="2" y="2" width="15" height="8" rx="1.5" fill="currentColor" />
                                <path d="M22 4v4a1.5 1.5 0 0 0 0-4z" fill="currentColor" fillOpacity="0.4" />
                              </svg>
                            </div>
                          </div>

                          {/* Enhanced Mobile App Content */}
                          <div className="px-5 pb-8 pt-3 space-y-4 bg-[#f5f5f7] min-h-[640px]">
                            {/* Header */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                              className="flex items-center justify-between p-5 bg-white rounded-2xl shadow-sm border border-gray-100 mt-14"
                            >
                              <div>
                                <h3 className="font-bold text-[17px] text-gray-900 leading-tight">
                                  Installation Orders
                                </h3>
                                <div className="flex items-center gap-2 mt-1.5">
                                  <motion.div
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className="w-2 h-2 bg-green-500 rounded-full"
                                  />
                                  <p className="text-[13px] text-gray-500">12 Active jobs</p>
                                </div>
                              </div>
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 10 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                                style={{ background: "#236DD9" }}
                              >
                                <UserCheck className="w-6 h-6 text-white" />
                              </motion.div>
                            </motion.div>

                            {/* Stats */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.7 }}
                              className="grid grid-cols-2 gap-4"
                            >
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                              >
                                <div className="text-[28px] font-bold text-gray-900 leading-none mb-1.5">
                                  <CounterAnimation value={23} duration={1500} />
                                </div>
                                <div className="text-[13px] text-gray-500">Active orders</div>
                              </motion.div>
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                              >
                                <div className="text-[28px] font-bold text-gray-900 leading-none mb-1.5">
                                  <CounterAnimation value={15} duration={1500} />
                                </div>
                                <div className="text-[13px] text-gray-500">Completed orders</div>
                              </motion.div>
                            </motion.div>

                            {/* Recent Orders */}
                            <div className="space-y-3.5">
                              <div className="flex items-center justify-between px-0.5 pt-0.5">
                                <h4 className="font-bold text-[15px] text-gray-900">Recent Orders</h4>
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                >
                                  <RefreshCw className="w-4 h-4" style={{ color: "#236DD9" }} />
                                </motion.div>
                              </div>

                              {[
                                {
                                  address: "1234 Beverly Hills Dr",
                                  status: "scheduled",
                                  time: "2:00 PM",
                                  progress: 75,
                                  agent: "Sarah J.",
                                },
                                {
                                  address: "5678 Manhattan Beach",
                                  status: "in progress",
                                  time: "On-site now",
                                  progress: 45,
                                  agent: "Mike C.",
                                },
                                {
                                  address: "9101 Santa Monica",
                                  status: "Completed",
                                  time: "Just finished",
                                  progress: 100,
                                  agent: "Lisa R.",
                                },
                              ].map((order, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, x: -20, scale: 0.95 }}
                                  animate={{ opacity: 1, x: 0, scale: 1 }}
                                  transition={{ delay: 0.9 + index * 0.1 }}
                                  whileHover={{
                                    x: 4,
                                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                                    scale: 1.02,
                                  }}
                                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
                                >
                                  <div className="flex items-center gap-3.5">
                                    <MapPin className="w-8 h-8 flex-shrink-0" style={{ color: "#236DD9" }} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-gray-900 truncate">
                                        {order.address}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {order.agent} • {order.time}
                                      </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                      <div className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-700 bg-gray-100 whitespace-nowrap">
                                        {order.status}
                                      </div>
                                      <div className="text-[11px] text-gray-500 font-medium">
                                        {order.progress}%
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>

                            {/* Quick Actions */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.3 }}
                              className="grid grid-cols-2 gap-3.5 pt-2"
                            >
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="text-white p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-md"
                                style={{ background: "#236DD9" }}
                              >
                                <ClipboardList className="w-4 h-4" />
                                New Order
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white border border-gray-200 text-gray-500 p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-sm"
                              >
                                <Search className="w-4 h-4" />
                                Search
                              </motion.button>
                            </motion.div>
                          </div>

                          {/* Home Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[110px] h-[4px] bg-black/80 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Enhanced Floating Notifications */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="absolute -right-16 top-16 bg-white rounded-2xl shadow-2xl p-2.5 border-2 max-w-52 z-20"
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </motion.div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Installation Complete!</p>
                        {/* <p className="text-xs text-gray-600">Photos uploaded ✓</p> */}
                      </div>
                    </div>
                  </motion.div>

                  {/* Additional Floating Element - New Order Alert */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: -20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    transition={{ delay: 2, duration: 0.5 }}
                    className="absolute -left-14 bottom-8 rounded-2xl shadow-2xl p-3 max-w-48 z-20"
                    style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
                  >
                    <div className="flex items-center gap-2 text-white">
                      <motion.div
                        animate={{
                          rotate: [0, 15, -15, 0],
                          scale: [1, 1.1, 1],
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
                      >
                        <Bell className="w-3 h-3" />
                      </motion.div>
                      <div>
                        <p className="text-xs font-bold">New Order Alert</p>
                        <p className="text-xs opacity-90">Beverly Hills install</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating Action Button */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 2.5, duration: 0.5 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute -bottom-4 right-1 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-2xl z-20 cursor-pointer"
                    style={{ background: "#f58b01" }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 180, 360] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    >
                      <Settings className="w-6 h-6 text-white" />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Benefits Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Brokerages Choose RealEsy
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                RealEsy is more than an order tool — it's a complete platform for purchasing,
                managing, and installing real estate signs.
                <br />
                From ordering new signage to scheduling installations, tracking inventory, and
                verifying completion, brokerages can manage every step with confidence, efficiency,
                and control.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-6 rounded-2xl border border-gray-200 text-center group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={benefit.icon} size={36} alt={benefit.title} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* FAQ Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl text-gray-700">
                Get answers to common questions about our installation order system.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <AppIcon name="faqQuestion" size={28} alt="FAQ" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              className="absolute w-8 h-8"
            >
              {i % 2 === 0 ? (
                <MapPin className="w-full h-full text-white" />
              ) : (
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Take the hassle out of real estate signage installations.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              With RealEsy, you'll never lose track of your signs, miss an update, or wait on proof
              again.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center opacity-100">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white opacity-100 w-full sm:w-auto"
                >
                  Get Started
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
